<script lang="ts" setup>
import { computed, ref } from 'vue';
import { EntityTagList, PageHero } from '~/components';
import { AppTypography } from '~/components/base';
import { useSources } from '~/composables/useSources';

definePageMeta({ layout: 'default' });

useHead({ title: 'Sources' });
useSeoMeta({
  description:
    "The official, verified sources behind VoteWise's election information.",
  ogTitle: 'Sources | VoteWise',
  ogDescription:
    "The official, verified sources behind VoteWise's election information.",
});

const PAGE_SIZE = 5;

const { sources } = useSources();

const visibleCount = ref(PAGE_SIZE);
const visibleSources = computed(() =>
  sources.value.slice(0, visibleCount.value),
);
const hasMore = computed(() => visibleCount.value < sources.value.length);

function showMoreSources() {
  visibleCount.value += PAGE_SIZE;
}
</script>

<template>
  <div class="sources-page">
    <PageHero constrained max-width="max-w-3xl">
      <div class="sources-page__card" data-testid="sources-page">
        <AppTypography
          type="title-5"
          element="h1"
          class="text-color-primary hidden lg:block"
        >
          Sources
        </AppTypography>

        <ul class="sources-page__list">
          <li
            v-for="source in visibleSources"
            :key="source.id"
            class="sources-page__row"
          >
            <div class="flex items-center gap-3">
              <div class="min-w-0 flex-1">
                <AppTypography type="subheadline" element="p" class="mb-1">
                  {{ source.label }}
                </AppTypography>
                <AppTypography
                  v-if="source.url"
                  type="footnote"
                  element="p"
                  class="text-color-tertiary truncate"
                >
                  {{ source.url }}
                </AppTypography>
              </div>

              <a
                v-if="source.url"
                :href="source.url"
                target="_blank"
                rel="noopener"
                :aria-label="`Open ${source.label} in a new tab`"
                class="sources-page__external shrink-0"
              >
                <Icon name="solar:square-top-down-linear" size="24" />
              </a>
            </div>

            <EntityTagList :tags="source.tags" :show-type-icon="false" />
          </li>
        </ul>

        <div v-if="hasMore" class="sources-page__more flex justify-center">
          <button
            type="button"
            data-testid="show-more-sources"
            class="sources-page__more-link"
            @click="showMoreSources"
          >
            Show More
          </button>
        </div>
      </div>
    </PageHero>
  </div>
</template>

<style scoped lang="scss">
.sources-page {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: calc(100vh - var(--header-height));

  &__card {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;
    padding: $spacing-lg;
    border-radius: $radius-2xl;
    background-color: $surface-color-bg;

    @media (min-width: 1024px) {
      padding: $spacing-2xl;
    }

    @media (max-width: 639.98px) {
      border-radius: $spacing-lg;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
  }

  &__row {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;
    padding: $spacing-lg 0;
    border-bottom: 1px solid $border-color;

    &:last-child {
      border-bottom: none;
    }
  }

  &__external {
    display: flex;
    color: $brand-color;
    transition: color 0.15s ease;

    &:hover {
      color: $text-color-brand-shade-1;
    }
  }

  &__more {
    padding-top: $spacing-sm;
  }

  &__more-link {
    background: none;
    border: none;
    cursor: pointer;
    @include font-body-bold;
    color: $brand-color;
    text-decoration: underline;
    transition: color 0.15s ease;

    &:hover {
      color: $text-color-brand-shade-1;
    }

    &:focus-visible {
      outline: 2px solid $border-color-brand;
      outline-offset: 2px;
    }

    @media (max-width: 639.98px) {
      font-size: 14px;
    }
  }
}
</style>
